// The shared per-night key for a meeting's moments — and the 4am window boundary.
//
// A "night" runs from the meeting until the next 4:00am in the meeting's LOCAL
// time, so anything before 4am local counts as the night before. Both phones at
// one meeting compute the SAME key (same instant, same local offset), so it is a
// key they share even though their per-side meeting rows are separate. This is
// why moments key on the night — not on `met_date`, which is the UTC calendar
// date of each device's stamp and can land a late-night meeting on the wrong day.
//
// Permanent data layer (CLAUDE.md § The Data Layer Is Permanent): pure, no React,
// no storage — carries directly to the native app.

const DAY_CUTOFF_HOURS = 4; // the night runs until 4:00am local
const MS_PER_MIN = 60000;
const MS_PER_HOUR = 3600000;
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

const pad = (n) => String(n).padStart(2, '0');
const ymdUTC = (d) => `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
const ymdLocal = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

/**
 * The night's local calendar date as "YYYY-MM-DD".
 *
 * @param {string} metAtIso  The meeting instant as an ISO string (UTC), e.g.
 *   "2026-05-29T02:43:00.000Z". A bare calendar date ("2026-05-29", a legacy
 *   `met_date` with no instant) is returned unchanged — never run UTC-midnight
 *   math on it (database-tables.md § meetings).
 * @param {number|null|undefined} metUtcOffsetMinutes  Minutes EAST of UTC at the
 *   meeting (EDT = -240, India = +330). null/undefined → viewer-local, matching
 *   the existing display fallback. Moments only ever attach to new meetings that
 *   carry an offset, so the fallback never groups real moments.
 * @returns {string} "YYYY-MM-DD", or "" when the input can't be parsed.
 */
export const nightKey = (metAtIso, metUtcOffsetMinutes) => {
  if (!metAtIso || typeof metAtIso !== 'string') return '';
  if (DATE_ONLY.test(metAtIso)) return metAtIso;

  const ms = Date.parse(metAtIso);
  if (Number.isNaN(ms)) return '';

  const shifted = ms - DAY_CUTOFF_HOURS * MS_PER_HOUR;

  if (Number.isFinite(metUtcOffsetMinutes)) {
    // Move the shifted instant into the meeting's local frame, then read its
    // calendar date from UTC components (we are already in local time).
    return ymdUTC(new Date(shifted + metUtcOffsetMinutes * MS_PER_MIN));
  }
  // No stored offset (legacy pre-016 rows) → viewer-local.
  return ymdLocal(new Date(shifted));
};
